/**
 * Hook de capture GPS (PRD §6.1.7 — capture silencieuse, zéro friction).
 *
 * Deux profils :
 * - captureSilent()       : options PRD (rapide, basse batterie) pour les captures
 *                           en arrière-plan (formulaires, rafraîchissement au login).
 * - captureHighAccuracy() : pour les flux où l'utilisateur demande explicitement
 *                           sa position (recherche du Relais le plus proche).
 *
 * Les deux retournent { latitude, longitude, precision_meters } ou null —
 * elles ne throw jamais : un refus/échec GPS ne doit jamais casser un parcours
 * (le backend géocode l'adresse en fallback, source 'geocoded').
 */

import { useCallback } from 'react';

const SILENT_OPTIONS = {
  enableHighAccuracy: false, // Plus rapide, moins de batterie
  timeout: 5000, // Timeout court pour ne pas bloquer l'interface
  maximumAge: 60000, // Accepter une position récente (1 minute)
};

const HIGH_ACCURACY_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

function getPosition(options) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        precision_meters: Math.round(position.coords.accuracy),
      }),
      () => resolve(null),
      options,
    );
  });
}

export function useGeolocation() {
  const captureSilent = useCallback(() => getPosition(SILENT_OPTIONS), []);
  const captureHighAccuracy = useCallback(() => getPosition(HIGH_ACCURACY_OPTIONS), []);
  /**
   * État de la permission géolocalisation : 'granted' | 'prompt' | 'denied' |
   * 'unknown' (API Permissions indisponible) | 'unsupported' (pas de GPS).
   * Permet de ne JAMAIS déclencher le prompt système hors d'un contexte
   * d'intention (un refus est sticky par origine).
   */
  const getPermissionState = useCallback(async () => {
    if (!navigator.geolocation) return 'unsupported';
    if (!navigator.permissions?.query) return 'unknown';
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state;
    } catch {
      return 'unknown';
    }
  }, []);
  return { captureSilent, captureHighAccuracy, getPermissionState };
}
